import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { cn } from "../../lib/utils";

export default function BottomModal({
  children,
  visible,
  onClose,
  overlayStyle,
  modalStyle,
  fadeDurationMs = 300,
}: {
  children: ReactNode;
  visible: boolean;
  onClose?: () => void;
  overlayStyle?: CSSProperties;
  modalStyle?: CSSProperties;
  fadeDurationMs?: number;
}) {
  const [status, setStatus] = useState<
    "open" | "opening" | "closing" | "closed"
  >("closed");
  const isVisible = status !== "closed";

  useEffect(() => {
    if (visible && status !== "open") {
      setStatus("opening");
      const openTimer = setTimeout(() => {
        setStatus("open");
      }, fadeDurationMs);
      return () => {
        clearTimeout(openTimer);
      };
    }
    if (!visible && status !== "closed") {
      setStatus("closing");
      const closeTimer = setTimeout(() => {
        setStatus("closed");
      }, fadeDurationMs);
      return () => {
        clearTimeout(closeTimer);
      };
    }
    // Already settled open or closed: no fade is in flight, so there is
    // nothing to tear down.
    return;
  }, [visible]);

  return createPortal(
    <div
      className={cn(
        "box-border opacity-0 fixed top-0 left-0 w-full h-dvh z-999",
        isVisible ? "block" : "hidden"
      )}
      style={{
        animation:
          status === "open" || status === "opening"
            ? `fadeIn ${fadeDurationMs}ms ease-in-out forwards`
            : `fadeOut ${fadeDurationMs}ms ease-in-out forwards`,
      }}
    >
      <div
        className="size-[inherit] bg-[#000c]"
        style={overlayStyle}
        onClick={onClose}
      />

      <RemoveScroll enabled={isVisible}>
        <div
          className={cn(
            "bottom-modal-content box-border fixed z-1000 p-6 rounded-2xl flex flex-col gap-6 bg-white text-primary-black",
            "bottom-[0.5em] inset-x-[0.5em] w-auto",
            "md:bottom-auto md:left-1/2 md:right-auto md:top-1/2 md:w-full md:max-w-article-figure-panel"
          )}
          style={{
            transform: status === "closed" ? "translateY(100%)" : undefined,
            animation:
              status === "opening" || status === "open"
                ? "slideUp 300ms ease-out forwards"
                : status === "closing"
                  ? "slideDown 300ms ease-in forwards"
                  : undefined,
            ...modalStyle,
          }}
        >
          {children}
        </div>
      </RemoveScroll>
    </div>,
    document.body
  );
}
